import moment from 'moment'
import type { LocationDailyCalls } from 'app/components/ai-agent-voice-ops/voiceAgentOpsMetrics'
import type { LocationJson } from 'app/types/entities'
import { EMPTY_COUNTS, EMPTY_DAILY_TOTALS, type SmsDailyField } from './smsAgentOpsMetrics'
import type {
  SmsAgentOpsCounts,
  SmsAgentOpsDailyCounts,
  SmsStaffTakeoverRow,
  SmsWorkflowRow,
} from 'app/types/sms-agent-ops'

/**
 * TEMPORARY. Simulated SMS ops metrics until trackable-api serves them
 * (docs/plans/2026-09-25-sms-agent-ops-analytics.md, "Backend contract"). Seeded by
 * location and day, so numbers hold across reloads and a day's texts don't change when
 * the range widens. The three event lists on the tab are real queries, so their counts
 * won't match these. Delete this file when the store switches to the ops resolvers.
 */

// mulberry32 over a string hash: tiny, deterministic, good enough for mock data.
function seededRandom(seed: string): () => number {
  let state = 0
  for (let i = 0; i < seed.length; i++) state = (Math.imul(31, state) + seed.charCodeAt(i)) | 0
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Rounds up with probability equal to the fraction, so small expected counts still show up over a range. */
function stochasticRound(value: number, rand: () => number): number {
  const whole = Math.floor(value)
  return whole + (rand() < value - whole ? 1 : 0)
}

const between = (rand: () => number, min: number, max: number) => min + rand() * (max - min)

type LocationProfile = {
  hasSmsAgent: boolean
  dailyInbound: number
  /** Share of inbound threads the AI picks up, plus AI openers per day from workflows. */
  aiPickup: number
  dailyOpeners: number
  aiRepliesPerConversation: number
  replyRate: number
  bookRate: number
  takeoverRate: number
  failureRate: number
  unansweredRate: number
  optOutRate: number
  toolErrorRate: number
  latencyMs: number
  costPerTurn: number
  staffResponseMinutes: number
  externalShare: number
  voiceAgentShare: number
  /** Days before today the AI went quiet; undefined when it is still replying. */
  silentForDays?: number
  /** Previous-period multipliers, drawn independently so some locations get worse and others better. */
  previousTakeoverFactor: number
  previousUnansweredFactor: number
}

function profileFor(location: LocationJson): LocationProfile {
  const rand = seededRandom(`sms-profile:${location.id}`)
  // Roughly one location in five is "troubled" so the tables have outliers worth spotting.
  const troubled = rand() < 0.2
  const hasSmsAgent = !!location.products?.includes(`SMS Agent`)
  return {
    hasSmsAgent,
    dailyInbound: between(rand, 3, 32),
    aiPickup: between(rand, 0.35, 0.7),
    dailyOpeners: between(rand, 1, 8),
    aiRepliesPerConversation: between(rand, 1.4, 3.6),
    replyRate: between(rand, 0.35, 0.78),
    bookRate: between(rand, 0.08, 0.3),
    takeoverRate: troubled ? between(rand, 0.35, 0.62) : between(rand, 0.04, 0.26),
    failureRate: troubled ? between(rand, 0.05, 0.12) : between(rand, 0.005, 0.035),
    unansweredRate: troubled ? between(rand, 0.25, 0.45) : between(rand, 0.03, 0.2),
    optOutRate: between(rand, 0.002, 0.012),
    toolErrorRate: troubled ? between(rand, 0.05, 0.11) : between(rand, 0.004, 0.03),
    latencyMs: between(rand, 1800, 5200),
    costPerTurn: between(rand, 0.004, 0.014),
    staffResponseMinutes: troubled ? between(rand, 60, 240) : between(rand, 4, 75),
    externalShare: rand() < 0.3 ? between(rand, 0.02, 0.12) : 0,
    voiceAgentShare: location.products?.includes(`AI Agent`) ? between(rand, 0.01, 0.06) : 0,
    silentForDays: hasSmsAgent && rand() < 0.15 ? 2 + Math.floor(rand() * 6) : undefined,
    previousTakeoverFactor: between(rand, 0.65, 1.35),
    previousUnansweredFactor: between(rand, 0.65, 1.35),
  }
}

const TAKEOVER_CAUSE_WEIGHTS = { staffReply: 0.62, manualToggle: 0.28, optOut: 0.1 }
const FAILURE_GROUP_WEIGHTS = {
  undelivered: 0.42,
  carrierFailed: 0.24,
  noConsent: 0.14,
  quietHours: 0.08,
  stuckPending: 0.12,
}

type DayResult = { counts: SmsAgentOpsCounts; daily: Omit<SmsAgentOpsDailyCounts, `date`> }

/** One simulated day at a location. `takeoverFactor` and `unansweredFactor` shift the previous period. */
function simulateDay(
  location: LocationJson,
  profile: LocationProfile,
  day: moment.Moment,
  today: moment.Moment,
  takeoverFactor = 1,
  unansweredFactor = 1,
): DayResult {
  const counts = { ...EMPTY_COUNTS }
  const daily = { ...EMPTY_DAILY_TOTALS }
  if (day.isAfter(today, `day`)) return { counts, daily }

  const rand = seededRandom(`sms:${location.id}:${day.format(`YYYY-MM-DD`)}`)
  const round = (value: number) => stochasticRound(value, rand)
  const weekend = day.isoWeekday() >= 6 ? 0.35 : 1
  const noise = () => 0.6 + rand() * 0.8

  const inbound = round(profile.dailyInbound * weekend * noise())
  const inboundThreads = round(inbound * 0.55)
  const unanswered = round(inboundThreads * Math.min(1, profile.unansweredRate * unansweredFactor))

  const silentFrom =
    profile.silentForDays === undefined ? undefined : today.clone().subtract(profile.silentForDays, `days`)
  const aiActive = profile.hasSmsAgent && !(silentFrom && day.isSameOrAfter(silentFrom, `day`))
  const aiConversations = aiActive ? round(inboundThreads * profile.aiPickup + profile.dailyOpeners * weekend) : 0
  const agentTexts = round(aiConversations * profile.aiRepliesPerConversation)
  const replied = round(aiConversations * profile.replyRate)
  const bookings = round(replied * profile.bookRate)
  const takeovers = Math.min(aiConversations, round(aiConversations * profile.takeoverRate * takeoverFactor))
  const staffReplyTakeovers = round(takeovers * TAKEOVER_CAUSE_WEIGHTS.staffReply)
  const optOutTakeovers = Math.min(takeovers - staffReplyTakeovers, round(takeovers * TAKEOVER_CAUSE_WEIGHTS.optOut))
  const manualToggleTakeovers = takeovers - staffReplyTakeovers - optOutTakeovers

  const workflowTexts =
    round(profile.dailyInbound * 0.7 * weekend * noise()) + (aiActive ? round(profile.dailyOpeners) : 0)
  const staffTexts = round(inbound * 0.55 * noise())
  const localOutbound = agentTexts + workflowTexts + staffTexts
  const externalTexts = round(localOutbound * profile.externalShare)
  const voiceAgentTexts = round(localOutbound * profile.voiceAgentShare)
  const outbound = localOutbound + externalTexts + voiceAgentTexts

  const failed = round(outbound * profile.failureRate)
  const failures = { undelivered: 0, carrierFailed: 0, noConsent: 0, quietHours: 0, stuckPending: 0 }
  for (let i = 0; i < failed; i++) {
    let pick = rand()
    for (const [group, weight] of Object.entries(FAILURE_GROUP_WEIGHTS) as [keyof typeof failures, number][]) {
      pick -= weight
      if (pick <= 0) {
        failures[group]++
        break
      }
    }
  }
  const failedTexts = Object.values(failures).reduce((sum, n) => sum + n, 0)
  const optOuts = round(outbound * profile.optOutRate)
  const staffResponses = round(staffTexts * 0.4)

  Object.assign(counts, {
    inbound,
    outbound,
    agentTexts,
    workflowTexts,
    staffTexts,
    externalTexts,
    voiceAgentTexts,
    inboundThreads,
    unansweredThreads: unanswered,
    aiConversations,
    repliedConversations: replied,
    bookings,
    takeovers,
    staffReplyTakeovers,
    manualToggleTakeovers,
    optOutTakeovers,
    failedTexts,
    undeliveredTexts: failures.undelivered,
    carrierFailedTexts: failures.carrierFailed,
    noConsentTexts: failures.noConsent,
    quietHoursTexts: failures.quietHours,
    stuckPendingTexts: failures.stuckPending,
    optOuts,
    aiTurns: agentTexts,
    toolErrorTurns: round(agentTexts * profile.toolErrorRate),
    aiLatencyMsTotal: agentTexts * profile.latencyMs * noise(),
    aiCostUsd: agentTexts * profile.costPerTurn,
    staffResponses,
    staffResponseMinutesTotal: staffResponses * profile.staffResponseMinutes * noise(),
  })
  Object.assign(daily, {
    inbound,
    agentTexts,
    workflowTexts,
    staffTexts,
    otherTexts: externalTexts + voiceAgentTexts,
    staffReplyTakeovers,
    manualToggleTakeovers,
    optOutTakeovers,
    undeliveredTexts: failures.undelivered,
    carrierFailedTexts: failures.carrierFailed,
    noConsentTexts: failures.noConsent,
    quietHoursTexts: failures.quietHours,
    stuckPendingTexts: failures.stuckPending,
    optOuts,
  })
  return { counts, daily }
}

function addInto(target: SmsAgentOpsCounts, source: SmsAgentOpsCounts): void {
  for (const key of Object.keys(source) as (keyof SmsAgentOpsCounts)[]) target[key] += source[key]
}

// Marked as samples, since they sit next to real location names in a screenshot.
const SYNTHETIC_STAFF = [`Jordan Avery`, `Sam Rivera`, `Casey Morgan`, `Riley Chen`, `Taylor Brooks`, `Alex Patel`].map(
  (name) => `${name} (sample)`,
)

const SYNTHETIC_WORKFLOWS: { name: string; share: number; replyRate: number; optOutRate: number }[] = [
  { name: `New Patient Follow-Up`, share: 0.24, replyRate: 0.46, optOutRate: 0.008 },
  { name: `Missed Call Text Back`, share: 0.2, replyRate: 0.58, optOutRate: 0.004 },
  { name: `Unscheduled Treatment`, share: 0.16, replyRate: 0.22, optOutRate: 0.021 },
  { name: `Recall Reminder`, share: 0.15, replyRate: 0.31, optOutRate: 0.012 },
  { name: `Reactivation Campaign`, share: 0.13, replyRate: 0.12, optOutRate: 0.047 },
  { name: `Review Request`, share: 0.12, replyRate: 0.09, optOutRate: 0.015 },
]

export type MockSmsOps = {
  rows: { locationId: string; counts: SmsAgentOpsCounts; lastAgentTextDate?: string }[]
  daily: SmsAgentOpsDailyCounts[]
  locationDaily: LocationDailyCalls[]
  staffRows: SmsStaffTakeoverRow[]
  workflowRows: SmsWorkflowRow[]
}

/**
 * Every metric the tab needs for the given locations and range, shaped like the ops
 * resolvers the plan asks for: per-location counts with previous-period fields, daily
 * counts, per-location daily AI replies, staff takeovers and workflow outcomes.
 */
export function buildMockSmsOps(
  locations: LocationJson[],
  fromDate: string,
  toDate: string,
  previousFromDate: string,
  previousToDate: string,
  now: moment.Moment,
): MockSmsOps {
  // The viewer's calendar day, compared in the same plain-date space as the range strings.
  const today = moment.utc(now.format(`YYYY-MM-DD`))
  const dailyByDate = new Map<string, SmsAgentOpsDailyCounts>()
  const rows: MockSmsOps[`rows`] = []
  const locationDaily: LocationDailyCalls[] = []
  const staffRows: SmsStaffTakeoverRow[] = []
  const workflowRows: SmsWorkflowRow[] = []

  for (const location of locations) {
    const profile = profileFor(location)
    const counts = { ...EMPTY_COUNTS }
    const repliesByDate: Record<string, number> = {}
    let lastAgentTextDate: string | undefined

    for (let day = moment.utc(fromDate); day.isSameOrBefore(toDate, `day`); day.add(1, `day`)) {
      const date = day.format(`YYYY-MM-DD`)
      const result = simulateDay(location, profile, day, today)
      addInto(counts, result.counts)
      const dayTotals = dailyByDate.get(date) ?? { date, ...EMPTY_DAILY_TOTALS }
      for (const field of Object.keys(EMPTY_DAILY_TOTALS) as SmsDailyField[]) dayTotals[field] += result.daily[field]
      dailyByDate.set(date, dayTotals)
      if (result.counts.agentTexts) {
        repliesByDate[date] = result.counts.agentTexts
        lastAgentTextDate = date
      }
    }

    for (let day = moment.utc(previousFromDate); day.isSameOrBefore(previousToDate, `day`); day.add(1, `day`)) {
      const result = simulateDay(
        location,
        profile,
        day,
        today,
        profile.previousTakeoverFactor,
        profile.previousUnansweredFactor,
      )
      counts.previousAiConversations += result.counts.aiConversations
      counts.previousTakeovers += result.counts.takeovers
      counts.previousInboundThreads += result.counts.inboundThreads
      counts.previousUnansweredThreads += result.counts.unansweredThreads
    }

    if (!counts.inbound && !counts.outbound) continue

    const silentSince =
      profile.silentForDays === undefined ? undefined : today.clone().subtract(profile.silentForDays, `days`)
    const dayBefore = moment.utc(fromDate).subtract(1, `day`)
    rows.push({ locationId: location.id!, counts, lastAgentTextDate })

    // Each location runs its own few workflows, often under the same names as its neighbors'.
    // The location's workflow texts and opt-outs are split across them, so the table never
    // shows more than the Location Breakdown.
    const workflowRand = seededRandom(`sms-workflows:${location.id}`)
    const workflows = SYNTHETIC_WORKFLOWS.filter(() => workflowRand() < 0.65)
    const shareSum = workflows.reduce((sum, workflow) => sum + workflow.share, 0)
    const optOutWeightSum = workflows.reduce((sum, workflow) => sum + workflow.share * workflow.optOutRate, 0)
    const failureRate = counts.outbound ? counts.failedTexts / counts.outbound : 0
    for (const workflow of workflows) {
      const textsSent = Math.round((counts.workflowTexts * workflow.share) / shareSum)
      const threads = Math.round(textsSent * 0.82)
      if (!textsSent) continue
      workflowRows.push({
        workflowId: `mock-workflow-${location.id}-${workflow.name}`,
        workflowName: workflow.name,
        locationId: location.id!,
        locationName: location.name ?? ``,
        textsSent,
        threads,
        repliedThreads: Math.round(threads * workflow.replyRate),
        optOuts: Math.floor((counts.optOuts * workflow.share * workflow.optOutRate) / optOutWeightSum),
        failedTexts: Math.round(textsSent * failureRate),
      })
    }
    if (profile.hasSmsAgent) {
      locationDaily.push({
        locationId: location.id!,
        lastCallBeforeRange:
          silentSince && silentSince.isSameOrBefore(dayBefore, `day`)
            ? silentSince.clone().subtract(1, `day`).format(`YYYY-MM-DD`)
            : dayBefore.format(`YYYY-MM-DD`),
        callsByDate: repliesByDate,
      })
    }

    // Split the location's staff takeovers across a few people, unevenly, so one name leads.
    const staffTakeovers = counts.staffReplyTakeovers + counts.manualToggleTakeovers
    if (staffTakeovers > 0) {
      const rand = seededRandom(`sms-staff:${location.id}`)
      const people = SYNTHETIC_STAFF.slice(0, 1 + Math.floor(rand() * 4))
      const weights = people.map((_, i) => 1 / (i + 1 + rand()))
      const weightSum = weights.reduce((sum, w) => sum + w, 0)
      let replyLeft = counts.staffReplyTakeovers
      let toggleLeft = counts.manualToggleTakeovers
      people.forEach((userName, i) => {
        const last = i === people.length - 1
        const reply = last ? replyLeft : Math.round((counts.staffReplyTakeovers * weights[i]) / weightSum)
        const toggle = last ? toggleLeft : Math.round((counts.manualToggleTakeovers * weights[i]) / weightSum)
        replyLeft -= reply
        toggleLeft -= toggle
        if (!reply && !toggle) return
        staffRows.push({
          userId: `mock-user-${location.id}-${i}`,
          userName,
          locationId: location.id!,
          locationName: location.name ?? ``,
          staffReplyTakeovers: reply,
          manualToggleTakeovers: toggle,
          takeovers: reply + toggle,
          lastTakeoverDate: lastAgentTextDate,
        })
      })
    }
  }

  return {
    rows,
    daily: [...dailyByDate.values()],
    locationDaily,
    staffRows: staffRows.sort((a, b) => b.takeovers - a.takeovers),
    workflowRows,
  }
}
